import { useState } from 'react';

export default function FinalCTA() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/public/form/submit/landing-page-final-cta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', phone: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="py-12 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="bg-gradient-to-br from-[#F8F9FA] to-white border-2 border-[#C5A880] rounded-3xl p-6 sm:p-12 md:p-16">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 items-center">
            {/* Left: Photo and Text */}
            <div>
              {/* Photo */}
              <div className="mb-6 sm:mb-8">
                <img
                  src="https://static.readdy.ai/image/621f76ab0c5d0ae7827482fe9856efef/353a1b52fb77cf7e2c624e877b9048b3.png"
                  alt="Ирина Шарова-Ялынпала"
                  className="w-48 h-60 sm:w-64 sm:h-80 object-cover object-top rounded-2xl shadow-2xl mx-auto md:mx-0 border-4 border-[#C5A880]"
                />
                <div className="mt-3 text-center md:text-left">
                  <p className="text-sm sm:text-base font-bold text-[#1A2B4C] font-['Montserrat']">
                    Ирина Шарова-Ялынпала
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 font-['Montserrat'] leading-snug mt-1">
                    Маркетолог-стратег. Эксперт по смысловой упаковке бизнеса и конверсионным сайтам
                  </p>
                </div>
              </div>

              {/* Text */}
              <div>
                <h2 className="text-xl sm:text-3xl md:text-4xl font-bold text-[#1A2B4C] mb-4 sm:mb-6 leading-tight font-['Playfair_Display']">
                  Ваш бизнес заслуживает сайта, который работает так же усердно, как и вы
                </h2>
                <p className="text-sm sm:text-lg text-gray-700 leading-relaxed font-['Montserrat'] text-justify">
                  Закажите разработку у профессионального маркетолога и получите готовую воронку продаж через неделю.
                </p>
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8 border border-[#C5A880]/20">
              <h3 className="text-xl sm:text-2xl font-bold text-[#1A2B4C] mb-5 sm:mb-6 font-['Playfair_Display']">
                Связаться с нами
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" data-readdy-form id="landing-page-final-cta">
                {/* Name */}
                <div>
                  <label htmlFor="final-name" className="block text-sm font-semibold text-[#1A2B4C] mb-2 font-['Montserrat']">
                    Ваше имя *
                  </label>
                  <input
                    type="text"
                    id="final-name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A880] focus:border-transparent transition-all text-sm sm:text-base font-['Montserrat']"
                    placeholder="Иван Иванов"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="final-phone" className="block text-sm font-semibold text-[#1A2B4C] mb-2 font-['Montserrat']">
                    Телефон *
                  </label>
                  <input
                    type="tel"
                    id="final-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A880] focus:border-transparent transition-all text-sm sm:text-base font-['Montserrat']"
                    placeholder="+7 (999) 123-45-67"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="final-message" className="block text-sm font-semibold text-[#1A2B4C] mb-2 font-['Montserrat']">
                    Сообщение (необязательно)
                  </label>
                  <textarea
                    id="final-message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#C5A880] focus:border-transparent transition-all resize-none text-sm sm:text-base font-['Montserrat']"
                    placeholder="Расскажите о вашем проекте..."
                  />
                  <p className="text-xs text-gray-500 mt-1 font-['Montserrat']">
                    Максимум 500 символов
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full bg-gradient-to-r from-[#C5A880] to-[#B39770] text-white py-4 rounded-lg font-semibold text-sm sm:text-lg hover:from-[#B39770] hover:to-[#A28660] transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer font-['Montserrat']"
                >
                  {status === 'sending' ? 'Отправка...' : 'Отправить заявку'}
                </button>

                {/* Status Messages */}
                {status === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm font-['Montserrat']">
                    <i className="ri-checkbox-circle-fill mr-2"></i>
                    Спасибо! Ваша заявка отправлена. Мы свяжемся с вами в ближайшее время.
                  </div>
                )}

                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm font-['Montserrat']">
                    <i className="ri-error-warning-fill mr-2"></i>
                    Произошла ошибка. Пожалуйста, попробуйте позже.
                  </div>
                )}

                {/* Privacy Note */}
                <p className="text-xs text-gray-500 text-center font-['Montserrat']">
                  Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}